/**
 * Детальный модуль подраздела «Сельское хозяйство» (US-17 FR-3 … FR-7) —
 * одна карточка «Сельское хозяйство» из пяти блоков в фиксированном
 * порядке: производители → производство животноводства → численность
 * скота и птицы → посевная площадь → валовой сбор и урожайность
 * (FR-1.3). Вёрстка — по макету Figma: каждый блок это таблица в рамке,
 * серая шапка которой несёт «название · подпись», а у блока с двумя
 * величинами — ещё и заголовки колонок.
 *
 * Правила блоков, из-за которых они не рисуются одной таблицей:
 *  - **Единица подписывается у каждой строки** (FR-4.2): мясо и молоко в
 *    тоннах, яйца — в тыс. штук, и общий заголовок блока к строке яиц не
 *    относится. Единица уезжает в название блока («(голов)», «(га)»)
 *    только там, где она у всех строк одна, — этот случай вычисляется из
 *    ответа, а не зашивается в подпись.
 *  - **Итог приходит готовым из ответа** (US-18 FR-3.6) — сложением
 *    строк на экране он не собирается: пропуск одной категории дал бы
 *    заниженный итог без всякого признака. На макете он стоит левой
 *    ячейкой во всю высоту строк; у блока производства животноводства
 *    итога нет вовсе (FR-4.4).
 *  - **У каждого блока своя отчётная подпись** (FR-8.1), взятая из его
 *    же данных: поголовье подписывается датой состояния, а не периодом
 *    накопления (FR-5.3), посевная площадь — годом урожая с охватом и
 *    признаком предварительности (FR-6.3, FR-6.4), валовой сбор — годом
 *    уборки, который отстаёт от года посевных (FR-7.4). Различие дат —
 *    норма, а не ошибка (FR-8.2).
 *  - **Переходов по клику нет** (FR-10): ни один элемент не оформляется
 *    как интерактивный — вид кликабельного элемента, который никуда не
 *    ведёт, читается как поломка.
 *
 * Чего на макете нет и что поэтому снято с экрана: пояснительные сноски
 * блоков, строка реквизитов выпуска и величины вне итога (домашние
 * хозяйства населения, US-18 FR-6.3). Данные для них бэк по-прежнему
 * отдаёт.
 *
 * Выключенный супер-администратором блок не рендерится вовсе и места не
 * занимает — остальные смыкаются (FR-1.3, FR-9.3).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAgricultureView } from './agricultureView';
import { StatNoData, StatNumber, fmt, fmt1 } from './statValue';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { cn } from '../../../lib/cn';
import type {
  AgricultureCropAreaResponse,
  AgricultureCropYieldResponse,
  AgricultureHeadcountResponse,
  AgricultureLivestockProductionResponse,
  AgricultureMeasure,
  AgricultureProducersResponse,
  StatValue,
} from '../../../types/analytics';

interface BlockState<T> {
  data: T | null;
  loading: boolean;
}

/**
 * Короткая подпись охвата по КОДУ ответа (US-18 FR-3.5). Полный текст
 * источника («Только сельхозформирования: юридические лица, …») в шапку
 * блока не помещается и на макете дан свёрнутым, поэтому он уходит в
 * подсказку, а не теряется. Незнакомый код печатается как пришёл.
 */
const COVERAGE_SHORT: Record<string, string> = {
  ALL_CATEGORIES: 'allCategories',
  AGRICULTURAL_FORMATIONS: 'formations',
  AGRICULTURAL_ENTERPRISES: 'enterprises',
  IP_AND_PEASANT_FARMS: 'ipAndPeasant',
  ACTIVE_PRODUCERS: 'activeProducers',
};

/** Строка блока: подпись + величина со своей единицей. */
interface RowVM {
  key: string;
  label: string;
  value: StatValue | null;
}

interface BlockProps {
  title: string;
  /** Подпись после «·»: период, охват, признаки выпуска. */
  caption?: string | null;
  /** Полный текст охвата из ответа — подсказкой к свёрнутой подписи. */
  captionHint?: string | null;
  slug: string;
  /** Заголовки колонок величин — в той же серой шапке (макет). */
  columns?: string[];
  loading: boolean;
  /**
   * Уровень территории не обслуживается (US-18 FR-4, `level_supported`).
   * Отдельное состояние, а не «нет данных»: по сельскому округу и селу
   * показатель не ведётся вовсе, и «нет данных» обещало бы, что он
   * появится, как только выйдет выпуск.
   */
  levelUnsupported?: boolean;
  empty: boolean;
  /** Строки блока несут ячейку итога слева (`RowList` с `total`). */
  withTotal?: boolean;
  children: ReactNode;
}

/** Блок детального модуля: таблица в рамке с серой шапкой. */
const Block = ({
  title,
  caption,
  captionHint,
  slug,
  columns = [],
  loading,
  levelUnsupported = false,
  empty,
  withTotal = false,
  children,
}: BlockProps) => {
  const { t } = useTranslation();
  const placeholder = loading || levelUnsupported || empty;
  // Шапка тянется на все ячейки строк: без колонок строка — подпись и
  // величина (+ итог слева), и одна ячейка заголовка сжимала бы его в
  // первую колонку, оставляя справа пустую полосу.
  const titleSpan = (columns.length > 0 ? 1 : 2) + (withTotal ? 1 : 0);

  return (
    <section
      data-testid={`block-${slug}`}
      className="overflow-hidden rounded-xl border border-surface-border"
    >
      <Table>
        <TableHeader>
          <TableRow className="border-surface-border bg-surface-muted/60 hover:bg-transparent">
            <TableHead
              className="h-auto px-4 py-3 font-extrabold leading-snug text-text"
              // Подпись блока — своя у каждого (FR-8.1): период у одних,
              // дата состояния у других, год урожая у третьих.
              data-testid={`${slug}-period`}
              colSpan={titleSpan}
            >
              {title}
              {caption != null && caption !== '' && (
                <>
                  {' · '}
                  <span title={captionHint ?? undefined}>{caption}</span>
                </>
              )}
            </TableHead>
            {columns.map((column) => (
              <TableHead
                key={column}
                className="h-auto whitespace-nowrap px-4 py-3 text-right font-extrabold leading-snug text-text"
              >
                {column}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {placeholder ? (
            <TableRow className="border-surface-border hover:bg-transparent">
              <TableCell className="px-4 py-3" colSpan={titleSpan + columns.length}>
                {loading ? (
                  <BlockLoader />
                ) : levelUnsupported ? (
                  <StatNoData
                    text={t('analytics.agriculture.levelNotSupported')}
                    testId={`${slug}-level-unsupported`}
                  />
                ) : (
                  <StatNoData text={t('common.noData')} />
                )}
              </TableCell>
            </TableRow>
          ) : (
            children
          )}
        </TableBody>
      </Table>
    </section>
  );
};

interface RowListProps {
  rows: RowVM[];
  slug: string;
  format: (v: number) => string;
  /** Единица печатается у значения — когда у строк блока она разная. */
  withUnit: boolean;
  /** Готовый итог блока — левой ячейкой во всю высоту строк (макет). */
  total?: { value: StatValue; unit: string | null } | null;
}

/** Строки блока: подпись · величина (+ ячейка итога слева). */
const RowList = ({
  rows,
  slug,
  format,
  withUnit,
  total = null,
}: RowListProps) => (
  <>
    {rows.map((row, index) => (
      <TableRow
        key={row.key}
        data-testid={`${slug}-row-${row.key}`}
        className="border-surface-border hover:bg-transparent"
      >
        {total !== null && index === 0 && (
          // rowSpan, а не отдельная плашка над списком: на макете итог
          // стоит слева и держит всю высоту строк блока.
          <TableCell
            rowSpan={rows.length}
            data-testid={`${slug}-total`}
            className="w-24 border-r border-surface-border px-4 py-3 text-center align-middle"
          >
            <StatNumber
              value={total.value}
              format={format}
              className="block text-2xl leading-tight text-chart-indigo"
              testId={`${slug}-total-value`}
            />
            {total.unit != null && (
              <span className="mt-0.5 block text-xs text-text-muted">
                {total.unit}
              </span>
            )}
          </TableCell>
        )}
        <TableCell className="px-4 py-3 font-normal leading-snug">
          {row.label}
        </TableCell>
        {/* Строка без значения показывает состояние величины, а не «0»
            (FR-9.1): ноль означал бы фактическое отсутствие производства. */}
        <TableCell className="whitespace-nowrap px-4 py-3 text-right">
          <StatNumber
            value={row.value}
            format={format}
            withUnit={withUnit}
            testId={`${slug}-${row.key}-value`}
          />
        </TableCell>
      </TableRow>
    ))}
  </>
);

export interface AgricultureDetailProps {
  producers: BlockState<AgricultureProducersResponse>;
  production: BlockState<AgricultureLivestockProductionResponse>;
  heads: BlockState<AgricultureHeadcountResponse>;
  sownArea: BlockState<AgricultureCropAreaResponse>;
  cropYield: BlockState<AgricultureCropYieldResponse>;
  /** Видимость блоков — реестр витрины (US-09 FR-1, US-18 FR-11.2). */
  show: {
    producers: boolean;
    production: boolean;
    heads: boolean;
    sownArea: boolean;
    cropYield: boolean;
  };
}

export const AgricultureDetail = ({
  producers,
  production,
  heads,
  sownArea,
  cropYield,
  show,
}: AgricultureDetailProps) => {
  const { t } = useTranslation();
  const view = useAgricultureView();

  /**
   * Строка списка: подпись и величина берутся из ответа как есть (US-18
   * `MeasureOut`) — собственного словаря номенклатуры у экрана нет:
   * состав блоков задаёт источник, и вторая копия наименований
   * разошлась бы с ним на первом же пополнении справочника.
   */
  const rowsOf = (rows: AgricultureMeasure[]): RowVM[] =>
    rows.flatMap<RowVM>((row, index) => {
      const label = view.label(row.name_ru, row.name_kz);
      if (label === null) return [];
      return [
        { key: row.key ?? `row-${index}`, label, value: view.measure(row) },
      ];
    });

  const producerRows = rowsOf(producers.data?.categories ?? []);
  const productionRows = rowsOf(production.data?.rows ?? []);
  const headsRows = rowsOf(heads.data?.rows ?? []);
  // На экран идёт «всего» по культуре; `parts` — её составные части
  // (сельхозпредприятия / ИП и КХ, FR-10.2.1), и сложение с «всего» дало
  // бы двойной счёт, поэтому они здесь не разворачиваются.
  const sownAreaMeasures = (sownArea.data?.rows ?? []).flatMap((row) =>
    row.total === null ? [] : [row.total],
  );
  const sownAreaRows = rowsOf(sownAreaMeasures);

  // Строки таблицы валового сбора — те же шесть культур в том же
  // порядке, что у посевной площади (FR-7.1); порядок задаёт ответ.
  const yieldRows = (cropYield.data?.rows ?? []).flatMap((row, index) => {
    const label = view.label(row.name_ru, row.name_kz);
    if (label === null) return [];
    return [
      {
        key: row.culture ?? `row-${index}`,
        label,
        gross: view.measure(row.gross_harvest),
        yield: view.measure(row.crop_yield),
      },
    ];
  });

  /** Подпись блока: части ответа через запятую, как на макете. */
  const caption = (...parts: Array<string | null>): string | null => {
    const text = parts
      .filter((part): part is string => part !== null && part !== '')
      .join(', ');
    return text === '' ? null : text;
  };

  /**
   * Единая единица блока — в название («Численность скота и птицы
   * (голов)»). Это НЕ подпись из локализации: у блока производства
   * животноводства единицы разные (тонны и тыс. штук), и там она
   * остаётся у каждой строки (FR-4.2). Решает ответ, а не вёрстка.
   */
  const sharedUnit = (rows: AgricultureMeasure[]): string | null => {
    const units = rows.map((row) => view.label(row.unit_ru, row.unit_kz));
    const first = units[0] ?? null;
    return first !== null && units.every((unit) => unit === first)
      ? first
      : null;
  };
  const titleWithUnit = (title: string, unit: string | null): string =>
    unit === null ? title : `${title} (${unit})`;

  /** Короткая подпись охвата + полный текст ответа подсказкой. */
  const coverageOf = (
    rows: AgricultureMeasure[],
  ): { short: string | null; full: string | null } => {
    const row = rows[0] ?? null;
    const full = view.coverage(row);
    const key = row?.coverage == null ? undefined : COVERAGE_SHORT[row.coverage];
    return {
      short:
        key === undefined
          ? full
          : t(`analytics.agriculture.coverageShort.${key}`),
      full,
    };
  };

  const producersUnit = sharedUnit(producers.data?.categories ?? []);
  const headsUnit = sharedUnit(heads.data?.rows ?? []);
  const sownAreaUnit = sharedUnit(sownAreaMeasures);
  const productionUnit = sharedUnit(production.data?.rows ?? []);
  const sownAreaCoverage = coverageOf(sownAreaMeasures);

  /**
   * Готовые итоги блоков (US-18 FR-3.6). Ячейка рисуется только при
   * значении: у поголовья `complete=false` означает, что по одному из
   * видов данных нет и сумма была бы заниженной — сервер отдаёт `null`,
   * и крупное «нет данных» вместо итога читалось бы как поломка блока.
   */
  const totalCell = (
    measure: AgricultureMeasure | null | undefined,
  ): { value: StatValue; unit: string | null } | null => {
    const value = view.measure(measure);
    return value === null || value.value === null
      ? null
      : { value, unit: value.unit };
  };
  const producersTotal = totalCell(producers.data?.total);
  const headsTotal = totalCell(heads.data?.total);

  // Левая и правая колонки карточки: порядок блоков внутри колонки — из
  // §«Дизайн» US-17, общий порядок чтения — FR-1.3. Выключенный блок в
  // колонку не попадает, и пустая колонка места не занимает.
  const left: ReactNode[] = [];
  const right: ReactNode[] = [];

  if (show.producers) {
    left.push(
      <Block
        key="producers"
        slug="agriculture-producers"
        title={titleWithUnit(
          t('analytics.agriculture.producers.title'),
          producersUnit,
        )}
        // Показатель «на дату»: подписывается датой состояния, периода
        // накопления у него в ответе нет вовсе (US-18 FR-2.2). «Действующие»
        // — набор из ответа: у источника есть параллельный набор
        // «зарегистрированные» с близкими значениями (FR-6.2).
        caption={caption(
          view
            .label(
              producers.data?.set_kind_ru ?? null,
              producers.data?.set_kind_kz ?? null,
            )
            ?.toLocaleLowerCase() ?? null,
          view.stateDate(producers.data?.state_date)?.label ?? null,
        )}
        loading={producers.loading}
        levelUnsupported={producers.data?.level_supported === false}
        empty={
          producers.data?.has_data === false ||
          (producerRows.length === 0 && producersTotal === null)
        }
        withTotal={producersTotal !== null && producerRows.length > 0}
      >
        <RowList
          rows={producerRows}
          slug="agriculture-producers"
          format={fmt}
          withUnit={producersUnit === null}
          total={producersTotal}
        />
      </Block>,
    );
  }

  if (show.production) {
    left.push(
      <Block
        key="production"
        slug="agriculture-production"
        title={titleWithUnit(
          t('analytics.agriculture.production.title'),
          productionUnit,
        )}
        caption={view.periodInline(production.data?.period)}
        loading={production.loading}
        levelUnsupported={production.data?.level_supported === false}
        empty={production.data?.has_data === false || productionRows.length === 0}
      >
        {/* Итога у блока нет (FR-4.4): сумма тонн и тыс. штук смысла не
            имеет, и единица подписывается у каждой строки. */}
        <RowList
          rows={productionRows}
          slug="agriculture-production"
          format={fmt1}
          withUnit={productionUnit === null}
        />
      </Block>,
    );
  }

  if (show.heads) {
    left.push(
      <Block
        key="heads"
        slug="agriculture-heads"
        title={titleWithUnit(t('analytics.agriculture.heads.title'), headsUnit)}
        // Подпись — последняя доступная дата состояния из данных блока, а
        // не период сводного модуля (FR-5.3, FR-5.4): состояние на
        // 1 августа отдаётся и тогда, когда плашка показывает январь–июль.
        caption={view.stateDate(heads.data?.state_date)?.label ?? null}
        loading={heads.loading}
        levelUnsupported={heads.data?.level_supported === false}
        empty={
          heads.data?.has_data === false ||
          (headsRows.length === 0 && headsTotal === null)
        }
        withTotal={headsTotal !== null && headsRows.length > 0}
      >
        <RowList
          rows={headsRows}
          slug="agriculture-heads"
          format={fmt}
          withUnit={headsUnit === null}
          total={headsTotal}
        />
      </Block>,
    );
  }

  if (show.sownArea) {
    right.push(
      <Block
        key="sownArea"
        slug="agriculture-sown-area"
        title={titleWithUnit(
          t('analytics.agriculture.sownArea.title'),
          sownAreaUnit,
        )}
        // Охват, год посева и предварительность — из ответа (FR-10.2):
        // цифра, которая уточнится к концу года, без подписи читается как
        // окончательная, а год посева опережает год уборки (FR-10.5).
        caption={caption(
          sownAreaCoverage.short,
          sownArea.data?.harvest_year !== null &&
            sownArea.data?.harvest_year !== undefined
            ? t('analytics.agriculture.sownArea.harvestYear', {
                year: sownArea.data.harvest_year,
              })
            : null,
          sownArea.data?.is_preliminary === true
            ? t('analytics.agriculture.sownArea.preliminary')
            : null,
        )}
        captionHint={sownAreaCoverage.full}
        loading={sownArea.loading}
        levelUnsupported={sownArea.data?.level_supported === false}
        empty={sownArea.data?.has_data === false || sownAreaRows.length === 0}
      >
        <RowList
          rows={sownAreaRows}
          slug="agriculture-sown-area"
          format={fmt1}
          withUnit={sownAreaUnit === null}
        />
      </Block>,
    );
  }

  if (show.cropYield) {
    right.push(
      <Block
        key="yield"
        slug="agriculture-yield"
        title={t('analytics.agriculture.yield.title')}
        // Год уборки отстаёт от года посевных площадей — норма (FR-7.4).
        caption={
          cropYield.data?.harvest_year != null
            ? t('analytics.agriculture.yield.harvestYear', {
                year: cropYield.data.harvest_year,
              })
            : null
        }
        columns={[
          t('analytics.agriculture.yield.columnGross'),
          t('analytics.agriculture.yield.columnYield'),
        ]}
        loading={cropYield.loading}
        levelUnsupported={cropYield.data?.level_supported === false}
        empty={cropYield.data?.has_data === false || yieldRows.length === 0}
      >
        {yieldRows.map((row) => (
          <TableRow
            key={row.key}
            data-testid={`agriculture-yield-row-${row.key}`}
            className="border-surface-border hover:bg-transparent"
          >
            <TableCell className="px-4 py-3 font-normal leading-snug">
              {row.label}
            </TableCell>
            <TableCell className="whitespace-nowrap px-4 py-3 text-right">
              <StatNumber
                value={row.gross}
                format={fmt1}
                testId={`agriculture-yield-${row.key}-gross`}
              />
            </TableCell>
            <TableCell className="whitespace-nowrap px-4 py-3 text-right">
              <StatNumber
                value={row.yield}
                format={fmt1}
                testId={`agriculture-yield-${row.key}-yield`}
              />
            </TableCell>
          </TableRow>
        ))}
      </Block>,
    );
  }

  if (left.length === 0 && right.length === 0) return null;

  return (
    <Card className="shadow-card" data-testid="analytics-agriculture-detail">
      <CardHeader>
        <CardTitle>{t('analytics.agriculture.detail.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Две колонки — только от xl: на планшете контента ~670 px, и
            блоки с числовыми строками в половине ширины не читаются. */}
        <div
          className={cn(
            'grid grid-cols-1 gap-3.5',
            left.length > 0 && right.length > 0 && 'xl:grid-cols-2',
          )}
        >
          {left.length > 0 && <div className="space-y-3.5">{left}</div>}
          {right.length > 0 && <div className="space-y-3.5">{right}</div>}
        </div>
      </CardContent>
    </Card>
  );
};
